import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  clearSession,
  getUser,
  isAuthenticated as checkAuthenticated,
  isExpired,
  setAuthSession,
} from '../services/auth';

const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getUser());

  const logout = () => {
    clearSession();
    setUser(null);
  };

  // Keep session valid and auto-logout on expiry
  useEffect(() => {
    if (!user) return undefined;

    if (isExpired(user.expiresAt)) {
      logout();
      return undefined;
    }

    const timeout = user.expiresAt ? user.expiresAt - Date.now() : 0;
    const timerId = setTimeout(logout, Math.max(timeout, 0));

    return () => clearTimeout(timerId);
  }, [user]);

  // Ensure any stale session is cleared on mount
  useEffect(() => {
    if (!checkAuthenticated()) {
      logout();
    }
  }, []);

  const login = ({
    token,
    role,
    expiresIn,
    expiresAt,
    displayName,
    professionalId,
    hospitalId,
    patientId,
    patientEmail,
    photoFileBytes,
    photoFileName,
    userId,
  }) => {
    const session = setAuthSession({
      token,
      role,
      expiresInSeconds: expiresIn,
      expiresAt,
      displayName,
      professionalId,
      hospitalId,
      patientId,
      patientEmail,
      photoFileBytes,
      photoFileName,
      userId,
    });
  
    setUser(session);
  };
  

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user && !isExpired(user.expiresAt),
      login,
      logout,
    }),
    [user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

